/**
 * Pure rules behind payment-claim approval (design G and the detail page's
 * pending claim card): how long a claim has waited, what the balance becomes,
 * which amounts and reject reasons are acceptable, and what a bulk approval
 * did. Money stays in integer minor units; the server decides in the end.
 */
import type { Payment, RejectCode } from '../../internals/repository/InvoiceRepository';
import { ago, money, parseMoney } from './format';

/** A claim waiting longer than this is shown in the warn colour. */
export const WARN_AFTER_MS = 24 * 60 * 60 * 1000;

export function waiting(createdAt: number | undefined, now = Date.now()): { text: string; warn: boolean } {
  if (!createdAt) return { text: '—', warn: false };
  return { text: ago(createdAt, now), warn: now - createdAt > WARN_AFTER_MS };
}

/** Balance after approving `amountMinor`; never below zero. */
export function balanceAfter(balanceMinor: number, amountMinor: number): number {
  const b = Math.round(Number(balanceMinor) || 0);
  const a = Math.round(Number(amountMinor) || 0);
  return Math.max(0, b - Math.max(0, a));
}

/**
 * A different (lower) amount to approve: 1 minor unit up to the smaller of the
 * claim and the balance. Returns the amount in minor units, or the error copy.
 */
export function checkDifferentAmount(
  text: string, claimedMinor: number, balanceMinor: number, currency = 'LKR',
): { minor: number; error: string | null } {
  const minor = parseMoney(text);
  if (String(text ?? '').trim() === '' || Number.isNaN(minor)) return { minor: NaN, error: 'Enter an amount.' };
  if (minor < 1) return { minor, error: 'Enter an amount above zero.' };
  if (minor > claimedMinor) return { minor, error: `This is more than the customer claimed (${money(claimedMinor, currency)}).` };
  if (minor > Math.max(0, balanceMinor)) return { minor, error: `This is more than the balance of ${money(Math.max(0, balanceMinor), currency)}.` };
  return { minor, error: null };
}

export const REJECT_REASONS: { code: RejectCode; label: string }[] = [
  { code: 'NOT_RECEIVED', label: 'Not received' },
  { code: 'WRONG_AMOUNT', label: 'Wrong amount' },
  { code: 'DUPLICATE', label: 'Duplicate' },
  { code: 'OTHER', label: 'Other' },
];

export const REJECT_REASON_MAX = 280;

/** Null when the reject form can be sent; "Other" needs a written reason. */
export function checkReject(code: RejectCode | null | undefined, reason: string): string | null {
  if (!code || !REJECT_REASONS.some((r) => r.code === code)) return 'Choose a reason.';
  const text = String(reason ?? '').trim();
  if (code === 'OTHER' && !text) return 'Tell the customer why.';
  if (text.length > REJECT_REASON_MAX) return `Keep the reason under ${REJECT_REASON_MAX} characters.`;
  return null;
}

/** "Not received", or the written reason for Other; for a rejected payment row and the timeline. */
export function rejectLabel(code?: string | null, reason?: string | null): string {
  if (code === 'OTHER') return (reason || '').trim() || 'Other';
  if (code === 'DOCUMENT_VOIDED') return 'Invoice voided';
  const hit = REJECT_REASONS.find((r) => r.code === code);
  return hit ? hit.label : (reason || '').trim() || 'No reason given';
}

/** What the slip is, from its storage key (or the signed URL's path); 'none' without a URL to show. */
export function slipKind(p: Pick<Payment, 'slipKey' | 'slipUrl'>): 'image' | 'pdf' | 'none' {
  if (!p.slipUrl) return 'none';
  const key = p.slipKey || p.slipUrl.split('?')[0];
  return /\.pdf$/i.test(key) ? 'pdf' : 'image';
}

// ── Errors ─────────────────────────────────────────────────────────────────

export const APPROVER_ONLY = 'Only the owner or an admin can approve payments.';

const CLAIM_ERRORS: Record<string, string> = {
  forbidden_role: APPROVER_ONLY,
  claim_not_pending: 'This claim was already approved or rejected.',
  amount_exceeds_balance: 'This is more than the balance due or the amount claimed.',
  invalid_state: 'This invoice can no longer take payments.',
  not_found: 'This claim no longer exists.',
  validation_failed: 'Check the details and try again.',
};

/** Banner copy for a failed approve or reject. */
export function claimErrorText(e: any): string {
  if (e?.status === 403 || e?.code === 'forbidden_role') return APPROVER_ONLY;
  const code = e?.code as string | undefined;
  if (code && CLAIM_ERRORS[code]) return CLAIM_ERRORS[code];
  return e?.message || 'Something went wrong. Try again.';
}

/** Short reason shown next to a claim that bulk approval skipped. */
export function bulkFailureText(code: string): string {
  switch (code) {
    case 'claim_not_pending': return 'Already approved or rejected';
    case 'amount_exceeds_balance': return 'More than the balance due';
    case 'invalid_state': return 'The invoice can no longer take payments';
    case 'not_found': return 'Claim not found';
    case 'forbidden_role': return APPROVER_ONLY;
    default: return code ? code.replace(/_/g, ' ') : 'Not approved';
  }
}

export interface BulkClaim { paymentId: string; amountMinor: number; label: string }
export interface BulkSummary {
  approvedCount: number;
  approvedMinor: number;
  failed: { paymentId: string; label: string; reason: string }[];
  text: string;
}

/** Sum of the claimed amounts for the confirm dialog. */
export const totalOf = (claims: { amountMinor: number }[]) =>
  claims.reduce((sum, c) => sum + Math.round(Number(c.amountMinor) || 0), 0);

/**
 * The toast and the failure list after approve-bulk. `claims` are the ones
 * sent, used for amounts and labels (invoice number and customer).
 */
export function bulkSummary(
  result: { approved: { paymentId: string }[]; failed: { paymentId: string; code: string }[] },
  claims: BulkClaim[],
  currency = 'LKR',
): BulkSummary {
  const byId = new Map(claims.map((c) => [c.paymentId, c]));
  const approved = result.approved || [];
  const approvedMinor = totalOf(approved.map((a) => ({ amountMinor: byId.get(a.paymentId)?.amountMinor ?? 0 })));
  const failed = (result.failed || []).map((f) => ({
    paymentId: f.paymentId,
    label: byId.get(f.paymentId)?.label ?? 'A claim',
    reason: bulkFailureText(f.code),
  }));
  const n = approved.length;
  const done = n === 0 ? 'No payments approved' : `${n} payment${n === 1 ? '' : 's'} approved · ${money(approvedMinor, currency)}`;
  const text = failed.length ? `${done}. ${failed.length} could not be approved.` : done;
  return { approvedCount: n, approvedMinor, failed, text };
}
